import Button from '@jetbrains/ring-ui/components/button/button'
import ButtonToolbar from '@jetbrains/ring-ui/components/button-toolbar/button-toolbar'
import {memo, useCallback, useMemo} from 'react'
import {shallowEqual} from 'react-redux'

import {usePermission} from '../../../../../hooks/usePermission'
import type {ProblemOccurrenceId, ProjectId} from '../../../../../types'
import {Permission} from '../../../../../types'
import {noop} from '../../../../../utils/empty'
import {assignProblemsInvestigations} from '../../BuildProblems.actions'
import {makeGetProblemOccurrencesByProblemOccurranceIds} from '../../BuildProblems.selectors'

import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'

type Props = {
  className?: string
  projectId: ProjectId
  problemOccurrencesIds: ReadonlyArray<ProblemOccurrenceId>
  onAction?: () => unknown
}

function InvestigationMuteButtons({
  className,
  projectId,
  problemOccurrencesIds,
  onAction = noop,
}: Props) {
  const dispatch = useAppDispatch()
  const getProblemOccurrencesByProblemOccurranceIds = useMemo(
    makeGetProblemOccurrencesByProblemOccurranceIds,
    [],
  )
  const hasPermissionForInvestigation = usePermission(Permission.ASSIGN_INVESTIGATION, projectId)
  const hasPermissionForMute = usePermission(Permission.MANAGE_BUILD_PROBLEMS, projectId)
  const problemOccurrences = useAppSelector(
    state => getProblemOccurrencesByProblemOccurranceIds(state, problemOccurrencesIds),
    shallowEqual,
  )
  const assign = useCallback(
    (fixMode = false) =>
      dispatch(assignProblemsInvestigations(projectId, problemOccurrences, fixMode, onAction)),
    [dispatch, onAction, problemOccurrences, projectId],
  )
  const assignInvestigationsOrMute = useCallback(() => assign(false), [assign])
  const fixInvestigations = useCallback(() => assign(true), [assign])
  const hasProblems = problemOccurrences.length > 0
  return hasPermissionForInvestigation || hasPermissionForMute ? (
    <ButtonToolbar className={className}>
      {hasPermissionForInvestigation && (
        <>
          <Button
            disabled={!hasProblems}
            onClick={assignInvestigationsOrMute}
            data-test-build-tests-list={'investigate-button'}
          >
            {'Investigate...'}
          </Button>
          <Button
            disabled={!hasProblems}
            onClick={fixInvestigations}
            data-test-build-tests-list={'fix-button'}
          >
            {'Fix…'}
          </Button>
        </>
      )}
      {hasPermissionForMute && (
        <Button
          disabled={!hasProblems}
          onClick={assignInvestigationsOrMute}
          data-test-build-tests-list={'fix-button'}
        >
          {'Mute…'}
        </Button>
      )}
    </ButtonToolbar>
  ) : null
}

export default memo<Props>(InvestigationMuteButtons)
